import { AsyncPipe, JsonPipe, Location } from '@angular/common';
import { Component, inject, ViewChild } from '@angular/core';
import { Timestamp } from '@angular/fire/firestore';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { ActivatedRoute } from '@angular/router';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { ToastrService } from 'ngx-toastr';
import { Observable, of, Subject, take, takeUntil } from 'rxjs';
import { v4 as uuidv4 } from 'uuid';
import { environment } from '../../../../../environments/environment';
import { AppExceptionModel } from '../../../../models/app-exception.model';
import { Grade } from '../../../../models/grade.school.model';
import { Group } from '../../../../models/group.model';
import {
  Lesson,
  LessonStatus,
  NotesType,
} from '../../../../models/lesson.school.model';
import { UserModel } from '../../../../models/user.model';
import { SchoolLessonService } from '../../../../services/lesson.school.service';
import { UtilsService } from '../../../../services/utils.service';
import * as lessonActions from '../../../../state/actions/lesson.school.actions';
import { AppState } from '../../../../state/appstate';
import * as gradeSelector from '../../../../state/selectors/grade.school.selector';
import * as groupSelector from '../../../../state/selectors/group.selector';
import { BtnBackComponent } from '../../../components/buttons/btn-back/btn-back.component';
import { BtnSubmitComponent } from '../../../components/buttons/btn-submit/btn-submit.component';
import { DlgConfirmComponent } from '../../../components/dialogs/dlg-confirm/dlg-confirm.component';
import { S3DocumentUploadComponent } from '../../../components/uploaders/s3-document-upload/s3-document-upload.component';

@Component({
  selector: 'add-lesson',
  standalone: true,
  imports: [
    BtnBackComponent,
    BtnSubmitComponent,
    AsyncPipe,
    JsonPipe,
    S3DocumentUploadComponent,
    FormsModule,
    ReactiveFormsModule,
  ],
  templateUrl: './add-lesson.component.html',
  styleUrl: './add-lesson.component.css',
})
export class AddLessonComponent {
  @ViewChild('uploader') uploader: S3DocumentUploadComponent | undefined;
  route: ActivatedRoute = inject(ActivatedRoute);
  location: Location = inject(Location);
  toastr: ToastrService = inject(ToastrService);
  store: Store<AppState> = inject(Store<AppState>);
  activatedRoute: ActivatedRoute = inject(ActivatedRoute);
  lessonService: SchoolLessonService = inject(SchoolLessonService);
  private actions$: Actions = inject(Actions);
  private dialog: MatDialog = inject(MatDialog);
  fb = inject(FormBuilder);

  groupId: string | null = this.activatedRoute.snapshot.paramMap.get('id')!;

  user: UserModel | null = null;
  group: Group | undefined = undefined;
  grades: Grade[] = [];

  uuid = uuidv4();

  pageForm: FormGroup;

  myRoles$: Observable<any> = new Observable();

  tags: string[] = [];

  preSignedUrlEndpoint: string = environment.AWS_SCHOOL_LESSON_SIGNEDURL;
  apiKey: string = environment.AWS_API_KEY;
  metadata: any = {};
  cloudFrontDomain = environment.AWS_GROUPS_CLOUDFRONT;

  lessonStatusFixed: boolean = false;

  lessonStatus = LessonStatus;
  notesType = NotesType;
  notesTypeList = [NotesType.pdf, NotesType.text];

  loading$: Observable<boolean> = of(false);

  exit$: Subject<void> = new Subject<void>();

  constructor() {
    this.route.paramMap.pipe(takeUntil(this.exit$)).subscribe((params) => {
      this.groupId = params.get('id');
    });

    this.myRoles$ = this.store.select(
      groupSelector.getMyRolesInGroup(this.groupId),
    );

    this.pageForm = this.fb.group({
      lessonName: new FormControl('', [
        Validators.required,
        Validators.minLength(3),
        Validators.maxLength(100),
      ]),
      gradeId: new FormControl([Validators.required]),
      avgAge: new FormControl([Validators.required]),
      lessonStatus: new FormControl(LessonStatus.available),
      notesType: new FormControl(NotesType.pdf),
      tagField: new FormControl(),
    });
  }

  ngOnInit(): void {
    this.store
      .select((state) => state.user.user)
      .pipe(takeUntil(this.exit$))
      .subscribe((user) => {
        this.user = user;
      });

    this.store
      .select(gradeSelector.getGradesByGroup(this.groupId))
      .pipe(takeUntil(this.exit$))
      .subscribe((grades) => {
        this.grades = grades;
        this.setFormGrade();
      });

    this.store
      .select(groupSelector.getSelectedGroup)
      .pipe(takeUntil(this.exit$))
      .subscribe((group) => {
        this.group = group;
        this.setFormGrade();
      });

    this.actions$
      .pipe(ofType(lessonActions.createSuccess), takeUntil(this.exit$))
      .subscribe(() => {
        this.loading$ = of(false);
        this.location.back();
      });

    this.actions$
      .pipe(ofType(lessonActions.createFailure), takeUntil(this.exit$))
      .subscribe((action) => {
        this.loading$ = of(false);
        this.onShowAlert(action.error as AppExceptionModel);
      });
  }

  setFormGrade() {
    if (
      this.group?.gradeId &&
      this.myRoles$
        .pipe(take(1))
        .subscribe((roles) => roles.management || roles.faculty)
    ) {
      this.pageForm.get('gradeId')?.setValue(this.group?.gradeId);
      if (this.group?.gradeId) {
        const grade = this.grades.find((g) => g.id === this.group?.gradeId);
        this.pageForm.get('avgAge')?.setValue(grade?.gradeAvgAge);
      }
    }
  }

  ngOnDestroy(): void {
    this.exit$.next();
    this.exit$.complete();
  }

  addTag() {
    this.pageForm.get('tagField')?.setErrors(null);
    if (this.pageForm.get('tagField')?.value === null) return;
    const fieldValue: string = this.pageForm.get('tagField')?.value.trim();
    if (fieldValue === '') return;
    if (fieldValue.includes(' ')) {
      console.log(`Invalid Tag: ${fieldValue}`);
      return this.pageForm.controls['tagField'].setErrors({
        invalidValue: true,
      });
    }
    if ((this.tags.length ?? 0) >= 10) {
      console.log(`Invalid Tag: ${fieldValue}`);
      return this.pageForm.controls['tagField'].setErrors({
        maxValuesAllowed: true,
      });
    }
    this.tags = [...this.tags, fieldValue];
    this.pageForm.get('tagField')?.patchValue('');
    this.pageForm.get('tagField')?.markAsPristine();
  }

  removeTag(event: any) {
    this.tags.splice(event.target.value, 1);
  }

  async create() {
    if (
      this.pageForm.invalid ||
      !this.group ||
      (this.pageForm.get('notesType')?.value === NotesType.pdf &&
        !this.uploader?.getIsAttached())
    ) {
      this.onShowAlert({
        title: 'Invalid Data',
        message:
          'Please check the form for missing or invalid data before submitting.',
      } as AppExceptionModel);
      return;
    }

    this.loading$ = of(true);

    if (this.pageForm.get('notesType')?.value === NotesType.pdf) {
      this.uploader?.setInputParams({
        groupId: this.groupId,
        id: this.uuid,
      });
      this.uploader?.setMetadata({
        groupId: this.groupId,
        id: this.uuid,
      });

      this.uploader
        ?.upload()
        ?.then((result) => {
          if (result?.successful) {
            this.createDatabaseEntry(
              this.uuid,
              result.successful[0]?.response?.uploadURL,
            );
          } else {
            this.loading$ = of(false);
            this.toastr.error(
              `Failed to upload document. ${result?.failed}`,
              'Document Upload Error',
            );
          }
        })
        .catch((error) => {
          this.loading$ = of(false);
          this.onShowAlert(error as AppExceptionModel);
        });
    } else {
      await this.createDatabaseEntry(this.uuid);
    }
  }

  async createDatabaseEntry(id: string, url?: string | undefined) {
    try {
      let data: Lesson = {
        lessonName: this.pageForm.get('lessonName')?.value,
        lessonStatus: this.pageForm.get('lessonStatus')?.value,
        groupId: this.groupId,
        gradeId: this.pageForm.get('gradeId')?.value,
        avgAge: this.pageForm.get('avgAge')?.value,
        notesType: this.pageForm.get('notesType')?.value,
        tags: this.tags,
        createdBy: this.user?.name,
        createdById: this.user?.uid,
        created: Timestamp.now(),
        modified: Timestamp.now(),
      } as Lesson;

      if (url)
        data.notesUrl = UtilsService.replaceDomain(url, this.cloudFrontDomain);

      this.store.dispatch(
        lessonActions.create({
          groupId: this.groupId!,
          lesson: {
            ...data,
            created: Timestamp.now(),
            modified: Timestamp.now(),
          } as Lesson,
        }),
      );
    } catch (error) {
      this.onShowAlert(error as AppExceptionModel);
      this.loading$ = of(false);
    }
  }

  onGradeChange(event: any) {
    const grade = this.grades.find((g) => g.id === event.target.value);
    this.pageForm.patchValue({ avgAge: grade?.gradeAvgAge });
  }

  onNotesTypeChange(event: any) {
    const selectedType: NotesType = event.target.value;
    this.pageForm.patchValue({ notesType: selectedType });
  }

  navigateBack() {
    this.location.back();
  }

  onShowAlert(error: AppExceptionModel) {
    let dialogConfig = new MatDialogConfig();

    dialogConfig.data = {
      title: error.title ?? 'Error Occurred',
      description: error.message,
      action: 'Okay',
      buttonColor: 'bg-primary-500',
      showType: 'error',
    };

    this.dialog.open(DlgConfirmComponent, dialogConfig);

    return;
  }
}
